import { useMemo } from "react";
import { DocType } from "./ReverifyIncomeModal/types";
import { CombinedIncome } from "../types/income";
import {
  PaystubDocumentPaystub,
  PaystubDocument,
  Document
} from "../types/verified/paystub";
import { PaystubItem } from "../types/verified/paystub";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";

type CriticalDataItem = {
  type: DocType;
  fileName: string;
};

// Helper function to check if paystub has valid critical data
const hasValidPaystubData = (paystub: PaystubDocumentPaystub): boolean => {
  const hasEmployerName = Boolean(paystub.employer.name);
  const hasEmployeeName = Boolean(paystub.employee.name);
  return hasEmployerName && hasEmployeeName;
};

// Helper function to get filename for paystub
const getPaystubFileName = (paystub: PaystubDocumentPaystub): string => {
  return (
    paystub.asset?.asset?.filename || `Paystub ${paystub.uuid.slice(0, 8)}`
  );
};

// Helper function to process paystubs from a source
const processPaystubsFromSource = (
  source: PaystubDocument,
  criticalItems: CriticalDataItem[]
): void => {
  source.pay_stubs.forEach((paystub) => {
    if (!hasValidPaystubData(paystub)) {
      const fileName = getPaystubFileName(paystub);

      // Avoid duplicates by checking if this file is already in the list
      if (!criticalItems.some((item) => item.fileName === fileName))
        criticalItems.push({
          type: DocType.Paystub,
          fileName
        });
    }
  });
};

// Helper function to process paystub document
const processPaystubDocument = (
  criticalItems: CriticalDataItem[],
  document?: Document | null
): void => {
  document?.income_sources.forEach((source) => {
    processPaystubsFromSource(source, criticalItems);
  });
};

// Helper function to process paystub income item
const processPaystubItem = (
  item: PaystubItem,
  criticalItems: CriticalDataItem[]
): void => {
  processPaystubDocument(criticalItems, item.data?.document);
};

export const CriticalDataMissingWarning = ({
  incomes
}: {
  incomes: CombinedIncome[];
}) => {
  const criticalMissingIncomeItems: CriticalDataItem[] = useMemo(() => {
    const criticalItems: CriticalDataItem[] = [];

    incomes.forEach((income) => {
      if (income.verified_source_type === "paystub")
        income.items.forEach((item) => {
          if (item.income_source_type === "paystub")
            processPaystubItem(item as PaystubItem, criticalItems);
        });
    });

    return criticalItems;
  }, [incomes]);

  if (!criticalMissingIncomeItems.length) return null;
  return (
    <div className="flex items-center py-6 gap-3.5 text-sm text-content-400 border-t border-alpha-40">
      <HugeiconsIcon
        icon={InformationCircleIcon}
        className="text-content-200 shrink-0"
        size="20"
        stroke="inherit"
      />
      <div>
        <p className="mb-2 font-medium">
          At least one of the applicant&apos;s uploaded documents is missing
          critical income details.
        </p>

        <p>
          {criticalMissingIncomeItems.map((item, index) => (
            <span key={index}>
              <span className="capitalize">{item.type}</span>
              <span className="font-bold text-content-1000">
                {item.fileName ? ` ${item.fileName}` : ""}
              </span>
              {criticalMissingIncomeItems.length - 1 === index ? "" : ", "}
            </span>
          ))}
        </p>
      </div>
    </div>
  );
};
